/**
 * React Query mutations for account management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, ApiError } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Export request status type
export type ExportStatus = 'queued' | 'processing' | 'ready' | 'failed';

export interface ExportRequest {
  id: string;
  status: ExportStatus;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  error: string | null;
  download_count: number;
}

export interface ExportStatusResponse {
  current: ExportRequest | null;
  history: ExportRequest[];
}

/**
 * Delete all chat history
 */
export function useDeleteChatHistory() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<{ success: true; deleted_sessions: number }>('/user/chat-history', {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: (data) => {
      toast.success(`Chat history deleted (${data.deleted_sessions} sessions removed)`);
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : 'Failed to delete chat history';
      toast.error(message);
    },
  });
}

/**
 * Delete all saved search queries
 */
export function useDeleteSearchHistory() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<{ success: true }>('/user/search-history', {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.setQueryData(queryKeys.user.searchHistory(user.id), []);
      }
      toast.success('Search history cleared');
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : 'Failed to clear search history';
      toast.error(message);
    },
  });
}

/**
 * Request data export
 */
export function useRequestExport() {
  const { user, session } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<{ success: true; request_id: string; status: ExportStatus; message: string }>('/user/export-request', {
        method: 'POST',
        token,
      });
    },
    onSuccess: (data) => {
      // Invalidate export status to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['exportStatus', user?.id] });
      toast.success(data.message || 'Export request received. Check back in a few minutes.');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.code === 'COOLDOWN_ACTIVE') {
          toast.error(error.message);
        } else if (error.code === 'EXPORT_IN_PROGRESS') {
          toast.error('An export request is already in progress');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to request export');
      }
    },
  });
}

/**
 * Get export status with polling
 * Polls every 3 seconds when there's an active export request
 */
export function useExportStatus(enabled = true) {
  const { user, session } = useAuth();
  const token = session?.access_token;

  return useQuery({
    queryKey: ['exportStatus', user?.id],
    queryFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<ExportStatusResponse>('/user/export-status', { token });
    },
    enabled: enabled && !!user && !!token,
    refetchOnMount: 'always',
    // Poll every 3 seconds when there's an active request
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.current?.status === 'queued' || data?.current?.status === 'processing') {
        return 3000; // 3 seconds
      }
      return false; // Stop polling
    },
    // Stop polling after 5 minutes (100 polls at 3 seconds)
    refetchIntervalInBackground: false,
    staleTime: 1000, // 1 second
  });
}

/**
 * Download export file
 */
export function useDownloadExport() {
  const { session } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();

  const downloadExport = async (requestId: string) => {
    if (!token) {
      toast.error('Not authenticated');
      return;
    }

    try {
      // Use fetch directly to handle binary download
      const response = await fetch(`${API_BASE_URL}/user/export-download/${requestId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(error.error || 'Download failed');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `filmple-export-${new Date().toISOString().split('T')[0]}.zip`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Export downloaded successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download export';
      toast.error(message);
    }
  };

  return { downloadExport };
}

/**
 * Deactivate account
 */
export function useDeactivateAccount() {
  const { user, session, signOut } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<{ success: true; message: string }>('/user/deactivate', {
        method: 'POST',
        token,
      });
    },
    onSuccess: async () => {
      // Sign out and redirect
      await signOut();
      navigate('/');
      toast.success('Your account has been deactivated. You have 30 days to restore it.');
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : 'Failed to deactivate account';
      toast.error(message);
    },
  });
}

/**
 * Restore deactivated account
 */
export function useRestoreAccount() {
  const { user, session, refreshProfile } = useAuth();
  const token = session?.access_token;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user || !token) {
        throw new Error('Not authenticated');
      }

      return apiFetch<{ success: true; message: string }>('/user/restore', {
        method: 'POST',
        token,
      });
    },
    onSuccess: async (data) => {
      // Refresh profile to clear deleted_at
      await refreshProfile();
      // Invalidate all user queries
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['settings', user?.id] });
      toast.success(data.message || 'Account restored successfully');
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : 'Failed to restore account';
      toast.error(message);
    },
  });
}
